"use client"

import * as React from "react"
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "../../lib/utils"
import { Button, buttonVariants } from "./button"
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from "lucide-react"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  locale,
  formatters,
  components,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar bg-oklch(1 0 0) p-2 [--cell-radius:var(--radius-md)] [--cell-size:2.9rem] dark:bg-oklch(0.145 0 0)",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-full overflow-hidden bg-white font-fordAntennaRegular", defaultClassNames.root),
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "mb-1 flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn("relative rounded-(--cell-radius)", defaultClassNames.dropdown_root),
        dropdown: cn(
          "absolute inset-0 bg-oklch(1 0 0) opacity-0 dark:bg-oklch(0.205 0 0)",
          defaultClassNames.dropdown
        ),
        caption_label: cn("font-medium select-none", captionLayout === "label"
          ? "text-sm"
          : "flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-oklch(0.556 0 0) dark:[&>svg]:text-oklch(0.708 0 0)", defaultClassNames.caption_label),
        table: "w-full border-collapse table-fixed",
        weekdays: cn("grid grid-cols-7", defaultClassNames.weekdays),
        weekday: cn(
          "flex-1 rounded-(--cell-radius) py-1 text-[0.85rem] font-normal text-oklch(0.556 0 0) select-none dark:text-oklch(0.708 0 0)",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 grid grid-cols-7 gap-y-1", defaultClassNames.week),
        week_number_header: cn("w-(--cell-size) select-none", defaultClassNames.week_number_header),
        week_number: cn(
          "text-[0.8rem] text-oklch(0.556 0 0) select-none dark:text-oklch(0.708 0 0)",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative h-[var(--cell-size)] w-full rounded-(--cell-radius) p-0 text-center align-middle select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)",
          props.showWeekNumber
            ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)"
            : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)",
          defaultClassNames.day
        ),
        range_start: cn(
          "relative isolate z-0 rounded-l-(--cell-radius) bg-oklch(0.97 0 0) after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-oklch(0.97 0 0) dark:bg-oklch(0.269 0 0) dark:after:bg-oklch(0.269 0 0)",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn(
          "relative isolate z-0 rounded-r-(--cell-radius) bg-oklch(0.97 0 0) after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-oklch(0.97 0 0) dark:bg-oklch(0.269 0 0) dark:after:bg-oklch(0.269 0 0)",
          defaultClassNames.range_end
        ),
        today: cn(
          "rounded-(--cell-radius) bg-oklch(0.97 0 0) text-oklch(0.145 0 0) data-[selected=true]:rounded-none dark:bg-oklch(0.269 0 0) dark:text-oklch(0.985 0 0)",
          defaultClassNames.today
        ),
        outside: cn(
          "text-oklch(0.556 0 0) aria-selected:text-oklch(0.556 0 0) dark:text-oklch(0.708 0 0) dark:aria-selected:text-oklch(0.708 0 0)",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-oklch(0.556 0 0) opacity-50 dark:text-oklch(0.708 0 0)",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (<div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />);
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (<ChevronLeftIcon className={cn("size-4", className)} {...props} />);
          }

          if (orientation === "right") {
            return (<ChevronRightIcon className={cn("size-4", className)} {...props} />);
          }

          return (<ChevronDownIcon className={cn("size-4", className)} {...props} />);
        },
        DayButton: ({ ...props }) => (
          <CalendarDayButton locale={locale} {...props} />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div
                className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props} />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "relative isolate z-10 flex h-[var(--cell-size)] w-full min-w-0 items-center justify-center rounded-lg border-0 text-[0.95rem] leading-none font-normal transition-colors hover:bg-[rgba(16,43,78,0.06)] hover:text-fordBlue group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-oklch(0.708 0 0) group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-oklch(0.708 0 0)/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-oklch(0.205 0 0) data-[range-end=true]:text-oklch(0.985 0 0) data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-oklch(0.97 0 0) data-[range-middle=true]:text-oklch(0.145 0 0) data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-oklch(0.205 0 0) data-[range-start=true]:text-oklch(0.985 0 0) data-[selected-single=true]:bg-oklch(0.205 0 0) data-[selected-single=true]:text-oklch(0.985 0 0) data-[selected-single=true]:hover:bg-fordBlue data-[selected-single=true]:hover:text-white dark:hover:text-oklch(0.145 0 0) [&>span]:text-xs [&>span]:opacity-70 dark:group-data-[focused=true]/day:border-oklch(0.556 0 0) dark:group-data-[focused=true]/day:ring-oklch(0.556 0 0)/50 dark:data-[range-end=true]:bg-oklch(0.922 0 0) dark:data-[range-end=true]:text-oklch(0.205 0 0) dark:data-[range-middle=true]:bg-oklch(0.269 0 0) dark:data-[range-middle=true]:text-oklch(0.985 0 0) dark:data-[range-start=true]:bg-oklch(0.922 0 0) dark:data-[range-start=true]:text-oklch(0.205 0 0) dark:data-[selected-single=true]:bg-oklch(0.922 0 0) dark:data-[selected-single=true]:text-oklch(0.205 0 0) dark:dark:hover:text-oklch(0.985 0 0)",
        defaultClassNames.day,
        className
      )}
      {...props} />
  );
}

export { Calendar, CalendarDayButton }
